import React, { useEffect, useState, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Switch,
  Upload,
  Image,
  Modal,
  Space,
  TimePicker
} from "antd";
import moment from "moment";
import { UploadOutlined, PlusOutlined } from "@ant-design/icons";
import ReactMapboxGl, { Marker } from "react-mapbox-gl";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import axios from "axios";
import request from "helper/request";
import { setMapboxRTLTextPlugin } from "../../../../helper/util";
import { DEFAULT_CENTER } from "../../../../configs/constants";
import AddressInput from "components/inputs/AddressInput";
import "./Locations.scss";

const { TextArea } = Input;

mapboxgl.workerClass =
  require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;
setMapboxRTLTextPlugin(mapboxgl);

const Map = ReactMapboxGl({
  accessToken: window.REACT_APP_MAPBOX_KEY
});

const LocationDetail = () => {
  const history = useHistory();
  const location = useLocation();
  const { locationId } = useParams();
  const isCreateMode = locationId === "new";
  const [currentLocation, setCurrentLocation] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [locationImage, setLocationImage] = useState(null);

  useEffect(() => {
    if (isCreateMode) {
      // Initialize empty form for create mode
      setCurrentLocation({
        locationName: "",
        address: "",
        lat: null,
        lng: null
      });
      form.setFieldsValue({
        locationName: "",
        descriptiveAddress: "",
        address: "",
        lat: null,
        lng: null,
        contactName: "",
        contactPhone: "",
        contactEmail: "",
        timeWindowStart: moment("08:00", "HH:mm"),
        timeWindowEnd: moment("17:00", "HH:mm"),
        waitingTime: 0,
        internalCustomerId: "",
        salesperson: "",
        note: "",
        isPublic: false
      });
    } else {
      const locationData = location.state?.locationData;
      if (locationData) {
        setCurrentLocation(locationData);
        initializeForm(locationData);
      } else {
        // Fallback: redirect back to locations list if no data provided
        message.error("Location data not available. Redirecting to locations list.");
        history.push("/locations");
      }
    }
  }, [location.state, history, isCreateMode, locationId]);

  const initializeForm = (locationData) => {
    form.setFieldsValue({
      locationName: locationData.locationName,
      descriptiveAddress: locationData.descriptiveAddress || "",
      address: locationData.address,
      lat: parseFloat(locationData.lat),
      lng: parseFloat(locationData.lng),
      contactName: locationData.contactName,
      contactPhone: locationData.contactPhone,
      contactEmail: locationData.contactEmail,
      timeWindowStart: locationData.timeWindowStart
        ? moment(locationData.timeWindowStart, "HH:mm")
        : null,
      timeWindowEnd: locationData.timeWindowEnd
        ? moment(locationData.timeWindowEnd, "HH:mm")
        : null,
      waitingTime: locationData.waitingTime,
      internalCustomerId: locationData.internalCustomerId,
      salesperson: locationData.salesperson,
      note: locationData.note,
      isPublic: locationData.isPublic
    });

    if (locationData.lat && locationData.lng) {
      setMarkerPosition([parseFloat(locationData.lng), parseFloat(locationData.lat)]);
    }

    // Set location image if available
    if (locationData.image) {
      setLocationImage(locationData.image);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const locationData = {
        ...values,
        locationName: values.locationName?.trim(),
        address: values.address?.trim(),
        contactName: values.contactName?.trim() || null,
        contactPhone: values.contactPhone?.trim(),
        contactEmail: values.contactEmail?.trim() || null,
        note: values.note?.trim() || null,
        descriptiveAddress: values.descriptiveAddress?.trim() || null,
        internalCustomerId: values.internalCustomerId?.trim() || null,
        salesperson: values.salesperson?.trim() || null,
        timeWindowStart: values.timeWindowStart
          ? values.timeWindowStart.format("HH:mm")
          : null,
        timeWindowEnd: values.timeWindowEnd ? values.timeWindowEnd.format("HH:mm") : null,
        waitingTime: parseInt(values.waitingTime) || 0
      };

      console.log("[LocationDetail] Saving location data:", locationData);

      if (isCreateMode) {
        // Create new location
        await request.post(`/saved-locations`, locationData);
        message.success("Location created successfully");
        // Redirect to the locations list or to the newly created location detail
        history.push("/locations");
      } else {
        // Update existing location
        console.log("[LocationDetail] Updating location with ID:", locationId);
        const response = await request.patch(
          `/saved-locations/${locationId}`,
          locationData
        );
        console.log("[LocationDetail] Update response:", response);
        message.success("Location updated successfully");

        // Update current location state with the response data
        const updatedLocation = {
          ...currentLocation,
          ...locationData,
          // Ensure descriptiveAddress is properly updated from the saved data
          descriptiveAddress: locationData.descriptiveAddress || ""
        };
        setCurrentLocation(updatedLocation);

        // Update form to ensure it reflects the saved state
        form.setFieldsValue({
          locationName: locationData.locationName,
          descriptiveAddress: locationData.descriptiveAddress || "",
          address: locationData.address,
          lat: parseFloat(locationData.lat),
          lng: parseFloat(locationData.lng),
          contactName: locationData.contactName,
          contactPhone: locationData.contactPhone,
          contactEmail: locationData.contactEmail,
          timeWindowStart: locationData.timeWindowStart
            ? moment(locationData.timeWindowStart, "HH:mm")
            : null,
          timeWindowEnd: locationData.timeWindowEnd
            ? moment(locationData.timeWindowEnd, "HH:mm")
            : null,
          waitingTime: locationData.waitingTime,
          internalCustomerId: locationData.internalCustomerId,
          salesperson: locationData.salesperson,
          note: locationData.note,
          isPublic: locationData.isPublic
        });
      }
    } catch (error) {
      message.error(
        isCreateMode ? "Failed to create location" : "Failed to update location"
      );
      console.error("Save error:", error);
    } finally {
      setLoading(false);
    }
  };

  const _onClickMap = (_map, e) => {
    const lngLatClick = e.lngLat;
    const newPosition = [lngLatClick.lng, lngLatClick.lat];
    setMarkerPosition(newPosition);

    axios
      .get(
        `${window.REACT_APP_API_ENDPOINT}/google/googlegeo?lat=${lngLatClick.lat}&lng=${lngLatClick.lng}`
      )
      .then((res) => {
        const address = res?.data?.results?.[0]?.formatted_address || "";
        if (address) {
          form.setFieldsValue({
            lat: lngLatClick.lat,
            lng: lngLatClick.lng,
            address: address
          });

          // Update currentLocation state to reflect the new address
          setCurrentLocation((prev) => ({
            ...prev,
            address: address,
            lat: lngLatClick.lat,
            lng: lngLatClick.lng
          }));
        }
      })
      .catch(function (error) {
        console.error("Error fetching address from coordinates:", error);
      });
  };

  const onMapLoad = (map) => {
    map.addControl(new mapboxgl.FullscreenControl(), "top-right");
  };

  const handleClickRef = useRef(_onClickMap);
  handleClickRef.current = _onClickMap;

  const handleBack = () => {
    history.push("/locations");
  };

  const handleAddressSelect = (addrObj) => {
    if (addrObj && addrObj.address && addrObj.lngLat && addrObj.lngLat.length === 2) {
      form.setFieldsValue({
        address: addrObj.address,
        lat: addrObj.lngLat[1],
        lng: addrObj.lngLat[0]
      });
      setMarkerPosition([addrObj.lngLat[0], addrObj.lngLat[1]]);

      // Update the currentLocation state as well for proper display
      setCurrentLocation((prev) => ({
        ...prev,
        address: addrObj.address,
        lat: addrObj.lngLat[1],
        lng: addrObj.lngLat[0]
      }));
    }
  };

  const validateImageFileType = (file) => {
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/heic"
    ];
    return allowedTypes.includes(file.type.toLowerCase());
  };

  const validateImageFileSize = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    return file.size <= maxSize;
  };

  const beforeImageUpload = (file) => {
    if (!validateImageFileType(file)) {
      message.error("Only JPG, JPEG, PNG, WebP, and HEIC image files are allowed");
      return Upload.LIST_IGNORE;
    }

    if (!validateImageFileSize(file)) {
      message.error("File size must be less than 5MB");
      return Upload.LIST_IGNORE;
    }

    setSelectedImageFile(file);
    setShowImageUploadModal(true);
    return false; // Prevent automatic upload
  };

  const handleImageUpload = async () => {
    if (!selectedImageFile) {
      message.error("Please select an image file");
      return;
    }

    if (isCreateMode) {
      message.warning("Please save the location first before uploading an image");
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("image", selectedImageFile);

      console.log("Uploading image for location:", locationId);
      const response = await request.post(
        `/saved-locations/${locationId}/image`,
        formData
      );
      console.log("Upload response:", response);

      // The response data is directly in response.data, and the URL is in response.data.url
      const imageUrl = response?.data?.url || response?.url;

      if (imageUrl) {
        message.success("Location image uploaded successfully!");
        // Update the image display with the new image URL
        setLocationImage(imageUrl);
        // Reset modal state
        setShowImageUploadModal(false);
        setSelectedImageFile(null);
      } else {
        console.error("No URL in response:", response);
        message.error("Upload succeeded but no image URL returned");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      console.error("Error response:", error.response);
      message.error(
        error.response?.data?.message || "Failed to upload image. Please try again."
      );
    } finally {
      setUploadingImage(false);
    }
  };

  if (!currentLocation) {
    return <div>Loading...</div>;
  }

  return (
    <div className="admin-location-detail">
      <div className="location-detail-header">
        <div className="breadcrumbs">
          <span
            className="breadcrumb-item"
            onClick={handleBack}
            style={{ cursor: "pointer" }}
          >
            LOCATIONS
          </span>
          <span className="breadcrumb-item">
            {isCreateMode ? "New Location" : currentLocation.locationName}
          </span>
        </div>
        <Button
          type="primary"
          className="save-button"
          onClick={handleSave}
          loading={loading}
        >
          {isCreateMode ? "Create" : "Save"}
        </Button>
      </div>

      <div className="detail-content">
        <div className="form-section">
          <Form form={form} layout="vertical" className="location-form">
            {!isCreateMode && (
              <div className="form-group">
                <div className="form-label">{currentLocation.locationName}</div>
                {currentLocation.descriptiveAddress && (
                  <div
                    className="form-descriptive"
                    style={{ fontStyle: "italic", color: "#666", marginBottom: "8px" }}
                  >
                    {currentLocation.descriptiveAddress}
                  </div>
                )}
                <div className="form-address">{currentLocation.address}</div>
                {currentLocation.lat && currentLocation.lng && (
                  <div className="coordinates">
                    Lat: {parseFloat(currentLocation.lat).toFixed(6)}, Lng:{" "}
                    {parseFloat(currentLocation.lng).toFixed(6)}
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <Form.Item
                label="Location Name"
                name="locationName"
                rules={[{ required: true, message: "Please enter location name" }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%"
                    }}
                  >
                    <span>Descriptive Address</span>
                    <span style={{ fontWeight: 400, color: "#888888" }}>(Optional)</span>
                  </div>
                }
                name="descriptiveAddress"
                tooltip="A human-friendly description of the location (e.g., 'Corner of 4th and Leeway' instead of '1736 1/2 Leeway Avenue Lower')"
              >
                <Input placeholder="e.g., Corner of 4th and Main Street" />
              </Form.Item>

              <Form.Item
                label="Address"
                name="address"
                rules={[{ required: true, message: "Please enter address" }]}
              >
                <AddressInput
                  placeholder="Enter address"
                  onSelect={handleAddressSelect}
                  address={{
                    address: form.getFieldValue("address"),
                    lngLat: [form.getFieldValue("lng"), form.getFieldValue("lat")]
                  }}
                />
              </Form.Item>

              <div style={{ display: "flex", gap: "16px" }}>
                <Form.Item
                  label="Latitude"
                  name="lat"
                  style={{ flex: 1 }}
                  rules={[{ required: true, message: "Please enter latitude" }]}
                >
                  <InputNumber style={{ width: "100%" }} precision={8} step={0.000001} />
                </Form.Item>

                <Form.Item
                  label="Longitude"
                  name="lng"
                  style={{ flex: 1 }}
                  rules={[{ required: true, message: "Please enter longitude" }]}
                >
                  <InputNumber style={{ width: "100%" }} precision={8} step={0.000001} />
                </Form.Item>
              </div>
            </div>

            <div className="form-group">
              <Form.Item label="Contact Name" name="contactName">
                <Input />
              </Form.Item>

              <Form.Item
                label="Contact Phone"
                name="contactPhone"
                rules={[{ required: true, message: "Please enter contact phone" }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%"
                    }}
                  >
                    <span>Contact Email</span>
                    <span style={{ fontWeight: 400, color: "#888888" }}>(Optional)</span>
                  </div>
                }
                name="contactEmail"
                rules={[{ type: "email", message: "Please enter valid email" }]}
              >
                <Input />
              </Form.Item>
            </div>

            <div className="form-group">
              <div style={{ display: "flex", gap: "16px" }}>
                <Form.Item
                  label="Time Window Start"
                  name="timeWindowStart"
                  style={{ flex: 1 }}
                >
                  <TimePicker
                    format="HH:mm"
                    style={{ width: "100%" }}
                    placeholder="Select time"
                    showNow={false}
                  />
                </Form.Item>

                <Form.Item
                  label="Time Window End"
                  name="timeWindowEnd"
                  style={{ flex: 1 }}
                >
                  <TimePicker
                    format="HH:mm"
                    style={{ width: "100%" }}
                    placeholder="Select time"
                    showNow={false}
                  />
                </Form.Item>
              </div>

              <Form.Item label="Waiting Time (minutes)" name="waitingTime">
                <InputNumber style={{ width: "100%" }} min={0} precision={0} />
              </Form.Item>
            </div>

            <div className="form-group">
              <Form.Item label="Internal Customer ID" name="internalCustomerId">
                <Input />
              </Form.Item>

              <Form.Item label="Salesperson" name="salesperson">
                <Input />
              </Form.Item>

              <Form.Item label="Notes" name="note">
                <TextArea rows={4} />
              </Form.Item>
            </div>

            <div className="form-group">
              <Form.Item label="Public Location" name="isPublic" valuePropName="checked">
                <Switch checkedChildren="Public" unCheckedChildren="Private" />
              </Form.Item>

              {!isCreateMode && (
                <Form.Item label="Location Image">
                  <div style={{ marginBottom: 16 }}>
                    <div
                      style={{
                        width: 200,
                        height: 200,
                        border: "2px dashed #d9d9d9",
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fafafa",
                        overflow: "hidden",
                        marginBottom: 8
                      }}
                    >
                      {locationImage ? (
                        <Image
                          src={locationImage}
                          alt="Location Image"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            display: "block"
                          }}
                          preview={true}
                        />
                      ) : (
                        <div style={{ textAlign: "center", color: "#8c8c8c" }}>
                          <PlusOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                          <div>No image uploaded</div>
                          <div style={{ fontSize: "12px" }}>Upload location image</div>
                        </div>
                      )}
                    </div>

                    <Upload
                      beforeUpload={beforeImageUpload}
                      showUploadList={false}
                      accept=".jpg,.jpeg,.png,.webp,.heic"
                    >
                      <Button icon={<UploadOutlined />} type="default">
                        {locationImage ? "Change Image" : "Upload Image"}
                      </Button>
                    </Upload>
                    <div style={{ marginTop: 8, fontSize: "12px", color: "#8c8c8c" }}>
                      Supported formats: JPG, JPEG, PNG, WebP, HEIC
                      <br />
                      Maximum size: 5MB
                    </div>
                  </div>
                </Form.Item>
              )}
              {isCreateMode && (
                <div
                  style={{
                    marginTop: 16,
                    padding: 12,
                    backgroundColor: "#e6f7ff",
                    borderRadius: 4
                  }}
                >
                  <p style={{ margin: 0, fontSize: "14px", color: "#1890ff" }}>
                    Note: You can upload a location image after creating the location.
                  </p>
                </div>
              )}
            </div>
          </Form>
        </div>

        <div className="map-section">
          <div className="map-container" style={{ height: "400px", width: "100%" }}>
            {isCreateMode || (currentLocation.lat && currentLocation.lng) ? (
              <Map
                center={
                  markerPosition ||
                  (isCreateMode
                    ? DEFAULT_CENTER
                    : [parseFloat(currentLocation.lng), parseFloat(currentLocation.lat)])
                }
                style="mapbox://styles/mapbox/streets-v11"
                zoom={isCreateMode ? [10] : [15]}
                onStyleLoad={onMapLoad}
                onClick={(map, event) => handleClickRef.current(map, event)}
                containerStyle={{
                  height: "100%",
                  width: "100%"
                }}
              >
                {markerPosition && (
                  <Marker coordinates={markerPosition}>
                    <img
                      style={{ width: "30px", height: "30px" }}
                      src="/icons/location.png"
                      alt="Location marker"
                    />
                  </Marker>
                )}
              </Map>
            ) : (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #d9d9d9"
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <img
                    src="/icons/map.svg"
                    alt="Map"
                    style={{
                      width: "64px",
                      height: "64px",
                      marginBottom: "16px",
                      opacity: 0.5
                    }}
                  />
                  <div style={{ fontSize: "16px", color: "#999" }}>
                    No coordinates available
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      <Modal
        title="Upload Location Image"
        open={showImageUploadModal}
        onCancel={() => {
          setShowImageUploadModal(false);
          setSelectedImageFile(null);
        }}
        width={500}
        footer={null}
      >
        <div style={{ marginBottom: 16 }}>
          <p>
            <strong>File Details:</strong>
          </p>
          {selectedImageFile && (
            <div
              style={{
                padding: 12,
                backgroundColor: "#f5f5f5",
                borderRadius: 4,
                marginBottom: 16
              }}
            >
              <div>
                <strong>Name:</strong> {selectedImageFile.name}
              </div>
              <div>
                <strong>Size:</strong> {(selectedImageFile.size / 1024).toFixed(1)} KB
              </div>
              <div>
                <strong>Type:</strong> {selectedImageFile.type}
              </div>
            </div>
          )}

          <div
            style={{
              padding: 12,
              backgroundColor: "#e6f7ff",
              borderRadius: 4,
              marginBottom: 16,
              fontSize: "12px"
            }}
          >
            <p style={{ margin: 0, marginBottom: 8 }}>
              <strong>Allowed file types:</strong>
            </p>
            <p style={{ margin: 0 }}>
              • Images: JPG, JPEG, PNG, WebP, HEIC
              <br />• Max size: 5MB
            </p>
          </div>
        </div>

        <div style={{ textAlign: "center" }}>
          <Space>
            <Button
              onClick={() => {
                setShowImageUploadModal(false);
                setSelectedImageFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={uploadingImage}
              disabled={!selectedImageFile}
              onClick={handleImageUpload}
              icon={<UploadOutlined />}
            >
              {uploadingImage ? "Uploading..." : "Upload Image"}
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default LocationDetail;
